import { useCallback, useEffect, useRef, useState } from "react";
import { CdnPlayerMessage, CdnPlayerTimeUpdate } from "./types";

const PLAYER_CONTEXT = "player.js";
const PLAYER_ORIGIN = "https://iframe.mediadelivery.net";
const PLAYER_VERSION = "0.0.11";
const PLAYER_EVENTS = ["play", "pause", "ended", "timeupdate"] as const;
const CDN_EVENTS_THAT_SHOW_CONTROLS = ["play", "pause", "seeking", "seeked", "ended"];
const REVEAL_DELAY_MS = 150;

type PlayerMethod =
  | "addEventListener"
  | "getCurrentTime"
  | "getDuration"
  | "getMuted"
  | "getPaused"
  | "mute"
  | "pause"
  | "play"
  | "setCurrentTime"
  | "unmute";

type PlayerCallback = (value: unknown) => void;

const parsePlayerMessage = (data: unknown): CdnPlayerMessage | null => {
  if (typeof data === "string") {
    try {
      return JSON.parse(data) as CdnPlayerMessage;
    } catch {
      return null;
    }
  }

  return typeof data === "object" && data !== null ? (data as CdnPlayerMessage) : null;
};

export const useCdnPlayer = () => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const playerTimeRef = useRef(0);
  const isSeekingRef = useRef(false);
  const isActivatedRef = useRef(false);
  const shouldUnmuteAfterAutoplayRef = useRef(true);
  const listenerIdRef = useRef(0);
  const callbacksRef = useRef(new Map<string, PlayerCallback>());
  const revealTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const [isVisible, setIsVisible] = useState(false);

  const sendMethod = useCallback((method: PlayerMethod, value?: unknown, callback?: PlayerCallback) => {
    const message: Record<string, unknown> = {
      context: PLAYER_CONTEXT,
      version: PLAYER_VERSION,
      method,
    };

    if (value !== undefined) message.value = value;

    if (callback) {
      const listener = `byb-player-${++listenerIdRef.current}`;
      callbacksRef.current.set(listener, callback);
      message.listener = listener;
    }

    iframeRef.current?.contentWindow?.postMessage(JSON.stringify(message), PLAYER_ORIGIN);
  }, []);

  const hideNativeControls = useCallback(() => {
    iframeRef.current?.contentWindow?.postMessage({ command: "toggleControls", parameter: false }, PLAYER_ORIGIN);
  }, []);

  const revealPlayer = useCallback(() => {
    clearTimeout(revealTimerRef.current);
    revealTimerRef.current = setTimeout(() => setIsVisible(true), REVEAL_DELAY_MS);
  }, []);

  const unmuteAfterAutoplay = useCallback(() => {
    if (!shouldUnmuteAfterAutoplayRef.current) return;

    shouldUnmuteAfterAutoplayRef.current = false;
    sendMethod("unmute");
    setIsMuted(false);
  }, [sendMethod]);

  const activatePlayer = useCallback(() => {
    if (isActivatedRef.current) return;
    isActivatedRef.current = true;

    PLAYER_EVENTS.forEach(event => sendMethod("addEventListener", event));
    hideNativeControls();
    sendMethod("mute");
    sendMethod("play");
    sendMethod("getCurrentTime", undefined, value => {
      playerTimeRef.current = value as number;
      setCurrentTime(value as number);
    });
    sendMethod("getDuration", undefined, value => setDuration(value as number));
    sendMethod("getPaused", undefined, value => setIsPlaying(!(value as boolean)));
    revealPlayer();
  }, [hideNativeControls, revealPlayer, sendMethod]);

  useEffect(() => {
    const handlePlayerMessage = (messageEvent: MessageEvent) => {
      if (messageEvent.origin !== PLAYER_ORIGIN || messageEvent.source !== iframeRef.current?.contentWindow) return;

      const message = parsePlayerMessage(messageEvent.data);
      if (!message) return;

      if (message.channel === "bunnystream") {
        if (CDN_EVENTS_THAT_SHOW_CONTROLS.includes(message.event ?? "")) hideNativeControls();

        const { status } = message;
        if (status) {
          if (status.currentTime !== undefined) {
            playerTimeRef.current = status.currentTime;
            if (!isSeekingRef.current) setCurrentTime(status.currentTime);
          }
          if (status.duration !== undefined) setDuration(status.duration);
          if (status.playing !== undefined) setIsPlaying(status.playing);
          if (status.playing) unmuteAfterAutoplay();
          if (status.ended) setIsPlaying(false);
        }
        return;
      }

      if (message.context !== PLAYER_CONTEXT) return;

      if (message.listener) {
        const callback = callbacksRef.current.get(message.listener);
        if (callback) {
          callbacksRef.current.delete(message.listener);
          callback(message.value);
        }
      }

      if (message.event === "ready") {
        activatePlayer();
        return;
      }

      if (CDN_EVENTS_THAT_SHOW_CONTROLS.includes(message.event ?? "")) hideNativeControls();
      if (message.event === "play") {
        setIsPlaying(true);
        unmuteAfterAutoplay();
      }
      if (message.event === "pause" || message.event === "ended") setIsPlaying(false);

      if (message.event === "timeupdate") {
        const { seconds, duration: updatedDuration } = message.value as CdnPlayerTimeUpdate;

        playerTimeRef.current = seconds;
        if (!isSeekingRef.current) setCurrentTime(seconds);
        setDuration(updatedDuration);
      }
    };

    window.addEventListener("message", handlePlayerMessage);

    return () => {
      window.removeEventListener("message", handlePlayerMessage);
      clearTimeout(revealTimerRef.current);
      callbacksRef.current.clear();
    };
  }, [activatePlayer, hideNativeControls, sendMethod, unmuteAfterAutoplay]);

  const togglePlayback = () => sendMethod(isPlaying ? "pause" : "play");

  const toggleMuted = () => {
    sendMethod(isMuted ? "unmute" : "mute");
    setIsMuted(!isMuted);
  };

  const previewSeek = (time: number) => {
    isSeekingRef.current = true;
    setCurrentTime(time);
  };

  const commitSeek = (time: number) => {
    sendMethod("setCurrentTime", time);
    playerTimeRef.current = time;
    isSeekingRef.current = false;
    setCurrentTime(time);
  };

  return {
    iframeRef,
    duration,
    currentTime,
    isPlaying,
    isMuted,
    isVisible,
    revealPlayer,
    togglePlayback,
    toggleMuted,
    previewSeek,
    commitSeek,
  };
};
